/**
 * Water bottle stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const waterBottleStickersPack: ToilePack = {
    slug: 'water-bottle-stickers',
    category: 'sticker',
    name: 'Water bottle stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'mountain', label: 'Mountain badge', subject: 'a round badge with a mountain range and a sun'},
        {key: 'hydrate', label: 'Hydrate slogan', subject: 'the word "HYDRATE" lettered in bold with a water drop'},
        {key: 'wave', label: 'Wave', subject: 'a curling wave in a circle'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'puffy', label: 'Puffy', style: 'puffy 3D sticker, pillowed shape with soft rounded edges, glossy vinyl surface', palette: 'Bright candy colours with a soft highlight', framing: 'Each sticker isolated on a plain ground, its raised edge showing'},
        {key: 'clear', label: 'Clear', style: 'clear sticker, the design printed on transparent film with a white underlayer, the edge visible', palette: 'Bright flat colour with a visible clear margin', framing: 'Each sticker isolated on a pale ground with the transparent edge showing'},
    ],
    directions: ['a sun badge instead', 'a whole sticker sheet', 'in ocean colors'],
    directionHint: 'try: a sun badge instead',
    seo: {
        title: 'Water Bottle Stickers: Mountain, Hydrate, Wave',
        metaDescription: 'Nine water bottle stickers — a mountain badge, a lettered hydrate slogan, a wave — in holographic, puffy, and clear finishes.',
        h1: 'Water bottle stickers: mountain, hydrate, and wave in three finishes',
        tagline: 'Nine bottle stickers, three designs, three finishes each.',
        intro: 'The sticker for the bottle: a mountain badge, a lettered hydrate slogan, a wave. Each comes holographic, puffy, and clear, matching the finishes that actually show up on a Hydro Flask or Stanley. Ask for a different slogan or a hiking trail name, and the same three finishes carry it.',
        useCases: [
            'decorating a Hydro Flask or Stanley',
            'a hydration-tracking bottle sticker',
            'a hiking or camping gear sticker',
            'a laptop or bottle sticker pack',
            'a gift topper for a reusable bottle',
        ],
        faq: [
            {q: 'Will these hold up on a bottle that gets washed?', a: 'These are print-at-home designs; for a bottle that gets regular washing, print onto waterproof vinyl sticker paper and consider a laminate layer.'},
            {q: "What's the difference between holographic, puffy, and clear?", a: 'Holographic describes a rainbow-sheen finish, puffy a domed 3D look, and clear a transparent background with only the design printed — pick whichever your printer stock matches.'},
            {q: 'Can I get my own hydration slogan or trail name?', a: 'Yes — the direction field takes any phrase or place name, rendered in the same three finishes.'},
            {q: 'Are these the right size for a standard bottle?', a: 'Each design prints at a size that suits most bottle diameters; resize before printing for a smaller cap or larger tumbler.'},
            {q: "How is this different from a Hydro Flask sticker pack I'd buy?", a: 'You get nine original designs to print and cut yourself instead of a fixed pre-cut sheet — pick the ones you want, at the size you want.'},
        ],
    },
}
